import { Action } from '@lib/plugin-redux-core';
import {
  Assignment,
  AssignmentProps,
  AttendeeDetail,
  EssayAnswer,
  FillInBlankGroupAnswer,
  MatchingAnswerGroup,
  MultipleChoiceAnswer,
  SingleChoiceAnswer,
  SpeakingAnswer,
} from '@module/assignment';
import { FormRelease, QuestionReleases } from '@module/form';
import {
  SubmissionPreviewPageState,
  SubmissionPreviewPageStatus,
} from './submission-preview.reducer';

export const handleGetSubmissionById = {
  executing: (state: SubmissionPreviewPageState): SubmissionPreviewPageState => {
    return {
      ...state,
      getSubmissionByIdStatus: SubmissionPreviewPageStatus.EXECUTING,
    };
  },
  success: (state: SubmissionPreviewPageState, action: Action): SubmissionPreviewPageState => {
    const { payload } = action;
    return {
      ...state,
      submission: payload,
      getSubmissionByIdStatus: SubmissionPreviewPageStatus.SUCCESS,
    };
  },
  error: (state: SubmissionPreviewPageState, action: Action): SubmissionPreviewPageState => {
    const { error } = action;
    return {
      ...state,
      getSubmissionByIdStatus: SubmissionPreviewPageStatus.ERROR,
      error,
    };
  },
  reset: (state: SubmissionPreviewPageState): SubmissionPreviewPageState => {
    return {
      ...state,
      getSubmissionByIdStatus: SubmissionPreviewPageStatus.RESET,
    };
  },
};

export const handleGetAssignmentByIdUseCase = {
  executing: (state: SubmissionPreviewPageState): SubmissionPreviewPageState => {
    return {
      ...state,
      getAssignmentByIdStatus: SubmissionPreviewPageStatus.EXECUTING,
    };
  },
  success: (state: SubmissionPreviewPageState, action: Action): SubmissionPreviewPageState => {
    const { payload } = action;
    return {
      ...state,
      assignment: payload as Assignment<AssignmentProps>,
      getAssignmentByIdStatus: SubmissionPreviewPageStatus.SUCCESS,
    };
  },
  error: (state: SubmissionPreviewPageState, action: Action): SubmissionPreviewPageState => {
    const { error } = action;
    return {
      ...state,
      getAssignmentByIdStatus: SubmissionPreviewPageStatus.ERROR,
      error,
    };
  },
  reset: (state: SubmissionPreviewPageState): SubmissionPreviewPageState => {
    return {
      ...state,
      getAssignmentByIdStatus: SubmissionPreviewPageStatus.RESET,
    };
  },
};

export const handleGetFormReleaseByIdUseCase = {
  executing: (state: SubmissionPreviewPageState): SubmissionPreviewPageState => {
    return {
      ...state,
      getFormReleaseByIdStatus: SubmissionPreviewPageStatus.EXECUTING,
    };
  },
  success: (state: SubmissionPreviewPageState, action: Action): SubmissionPreviewPageState => {
    const { payload } = action;

    return {
      ...state,
      formRelease: payload as FormRelease,
      getFormReleaseByIdStatus: SubmissionPreviewPageStatus.SUCCESS,
    };
  },
  error: (state: SubmissionPreviewPageState, action: Action): SubmissionPreviewPageState => {
    const { error } = action;
    return {
      ...state,
      error,
      getFormReleaseByIdStatus: SubmissionPreviewPageStatus.ERROR,
    };
  },
  reset: (state: SubmissionPreviewPageState): SubmissionPreviewPageState => {
    return {
      ...state,
      getFormReleaseByIdStatus: SubmissionPreviewPageStatus.RESET,
    };
  },
};

export const handleGetAttendeeByIdUseCase = {
  executing: (state: SubmissionPreviewPageState): SubmissionPreviewPageState => {
    return {
      ...state,
      getAttendeeByIdStatus: SubmissionPreviewPageStatus.EXECUTING,
    };
  },
  success: (state: SubmissionPreviewPageState, action: Action): SubmissionPreviewPageState => {
    const { payload } = action;
    return {
      ...state,
      getAttendeeByIdStatus: SubmissionPreviewPageStatus.SUCCESS,
      attendeeDetail: payload as AttendeeDetail,
    };
  },
  error: (state: SubmissionPreviewPageState, action: Action): SubmissionPreviewPageState => {
    const { error } = action;
    return {
      ...state,
      getAttendeeByIdStatus: SubmissionPreviewPageStatus.ERROR,
      error,
    };
  },
  reset: (state: SubmissionPreviewPageState): SubmissionPreviewPageState => {
    return {
      ...state,
      getAttendeeByIdStatus: SubmissionPreviewPageStatus.RESET,
    };
  },
};

export const handleGetQuestionReleasesUseCase = {
  executing: (state: SubmissionPreviewPageState): SubmissionPreviewPageState => {
    return {
      ...state,
      getQuestionReleasesStatus: SubmissionPreviewPageStatus.EXECUTING,
    };
  },
  success: (state: SubmissionPreviewPageState, action: Action): SubmissionPreviewPageState => {
    const { payload } = action;
    const questions = payload;
    const sortedQuestions = QuestionReleases.sortQuestions(questions);

    return {
      ...state,
      questionReleases: sortedQuestions,
      getQuestionReleasesStatus: SubmissionPreviewPageStatus.SUCCESS,
    };
  },
  error: (state: SubmissionPreviewPageState, action: Action): SubmissionPreviewPageState => {
    const { error } = action;
    return {
      ...state,
      getQuestionReleasesStatus: SubmissionPreviewPageStatus.ERROR,
      error,
    };
  },
  reset: (state: SubmissionPreviewPageState): SubmissionPreviewPageState => {
    return {
      ...state,
      getQuestionReleasesStatus: SubmissionPreviewPageStatus.RESET,
    };
  },
};

export const handleGetAnswersUseCase = {
  executing: (state: SubmissionPreviewPageState): SubmissionPreviewPageState => {
    return {
      ...state,
      getAnswersStatus: SubmissionPreviewPageStatus.EXECUTING,
    };
  },
  success: (state: SubmissionPreviewPageState, action: Action): SubmissionPreviewPageState => {
    const { payload } = action;

    return {
      ...state,
      answers: payload as (
        | EssayAnswer
        | SingleChoiceAnswer
        | FillInBlankGroupAnswer
        | MultipleChoiceAnswer
        | MatchingAnswerGroup
        | SpeakingAnswer
      )[],
      getAnswersStatus: SubmissionPreviewPageStatus.SUCCESS,
    };
  },
  error: (state: SubmissionPreviewPageState, action: Action): SubmissionPreviewPageState => {
    const { error } = action;
    return {
      ...state,
      error,
      getAnswersStatus: SubmissionPreviewPageStatus.ERROR,
    };
  },
};

export const handleUpdateSubmissionUseCase = {
  executing: (state: SubmissionPreviewPageState): SubmissionPreviewPageState => {
    return {
      ...state,
      updateSubmissionStatus: SubmissionPreviewPageStatus.EXECUTING,
    };
  },
  success: (state: SubmissionPreviewPageState, action: Action): SubmissionPreviewPageState => {
    const { submission } = action.payload;
    return {
      ...state,
      submission,
      updateSubmissionStatus: SubmissionPreviewPageStatus.SUCCESS,
    };
  },
  error: (state: SubmissionPreviewPageState, action: Action): SubmissionPreviewPageState => {
    const { error } = action;
    return {
      ...state,
      error,
      updateSubmissionStatus: SubmissionPreviewPageStatus.ERROR,
    };
  },
  reset: (state: SubmissionPreviewPageState): SubmissionPreviewPageState => {
    return {
      ...state,
      updateSubmissionStatus: SubmissionPreviewPageStatus.RESET,
    };
  },
};
